import { Model, Factory, Response } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randNumber } from '@ngneat/falso'
import type { MirageServer, MockServer } from '../../server.js'
import { createErrorResponse, sortable } from '../../utils.js'
import type { SchemaAttributes } from '@@/api/types'

type Mutable<T> = { -readonly [P in keyof T]: T[P] }
type ItemDiagnosisGroupMockData = Mutable<
  SchemaAttributes<'ItemDiagnosisGroup'>
>

export const model = Model as ModelDefinition<ItemDiagnosisGroupMockData>

export const factory = Factory.extend<ItemDiagnosisGroupMockData>({
  item_id() {
    return randNumber({ min: 1, max: 20 })
  },
  department_id() {
    return null
  },
  diagnosis_ids() {
    return []
  },
  item() {
    return null
  },
  department() {
    return null
  },
  diagnoses() {
    return []
  },
})

function getDiagnosesArray(
  schema: MirageServer['schema'],
  diagnosisIds: string[],
): Array<{ id: string; name: string }> {
  if (!diagnosisIds.length) {
    return []
  }

  return diagnosisIds
    .map((id) => {
      const diagnosis = schema.find('diagnosis', id)
      if (!diagnosis) {
        return null
      }
      return {
        id,
        name: diagnosis.attrs.label || diagnosis.attrs.code || '',
      }
    })
    .filter((d): d is { id: string; name: string } => d !== null)
}

function getItemObject(
  schema: MirageServer['schema'],
  itemId: number,
): ItemDiagnosisGroupMockData['item'] {
  const item = schema.find('organizationItem', String(itemId))
  if (!item) {
    return null
  }
  return {
    id: itemId,
    name: item.attrs.name || '',
    type: item.attrs.type_display || 'PROCEDURE',
  } as NonNullable<ItemDiagnosisGroupMockData['item']>
}

function getDepartmentObject(
  schema: MirageServer['schema'],
  departmentId: number | null,
): { id: number; name: string; is_central: boolean } | null {
  if (!departmentId) {
    return null
  }
  const department = schema.find('department', String(departmentId))
  if (!department) {
    return null
  }
  return {
    id: departmentId,
    name: department.attrs.name || '',
    is_central: department.attrs.is_central || false,
  }
}

function serializeGroup(group: {
  id?: string
  attrs: ItemDiagnosisGroupMockData
}) {
  return {
    id: group.id,
    type: 'ItemDiagnosisGroup',
    attributes: {
      item_id: group.attrs.item_id,
      item: group.attrs.item,
      department_id: group.attrs.department_id,
      department: group.attrs.department,
      diagnosis_ids: group.attrs.diagnosis_ids,
      diagnoses: group.attrs.diagnoses,
    },
  }
}

export function routes(server: MockServer) {
  server.get(
    '/catalog/management/item-diagnoses/v1',
    function (this: MirageServer, schema, request) {
      const allGroups = schema.all('itemDiagnosisGroup')
      const sortedGroups = sortable(allGroups, request, {
        item_name: (group) => group.attrs.item?.name ?? '',
        department: (group) => group.attrs.department?.name ?? '',
      })

      const searchFilter = request.queryParams['filter[search]']
      const departmentFilter = request.queryParams['filter[department_id]']

      let filteredModels = [...sortedGroups.models]

      if (searchFilter) {
        const searchValue = Array.isArray(searchFilter)
          ? searchFilter[0]
          : searchFilter
        const searchLower = searchValue.toLowerCase()
        filteredModels = filteredModels.filter((group) =>
          group.attrs.item?.name?.toLowerCase().includes(searchLower),
        )
      }

      if (departmentFilter) {
        const departmentValue = Array.isArray(departmentFilter)
          ? departmentFilter[0]
          : departmentFilter
        filteredModels = filteredModels.filter(
          (group) => group.attrs.department_id === Number(departmentValue),
        )
      }

      const pageParam = request.queryParams['page[number]']
      const pageSizeParam = request.queryParams['page[size]']
      const page = parseInt(
        Array.isArray(pageParam) ? pageParam[0] : pageParam || '1',
        10,
      )
      const pageSize = parseInt(
        Array.isArray(pageSizeParam) ? pageSizeParam[0] : pageSizeParam || '30',
        10,
      )
      const start = (page - 1) * pageSize
      const end = start + pageSize
      const paginatedModels = filteredModels.slice(start, end)
      const totalCount = filteredModels.length
      const totalPages = Math.ceil(totalCount / pageSize)

      return {
        data: paginatedModels.map(serializeGroup),
        meta: {
          pagination: {
            count: totalCount,
            pages: totalPages,
          },
        },
      }
    },
  )

  server.get(
    '/catalog/management/item-diagnoses/v1/:id',
    function (this: MirageServer, schema, request) {
      const group = schema.find('itemDiagnosisGroup', request.params.id)

      if (!group) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Item diagnosis group not found' }],
        })
      }

      return { data: serializeGroup(group) }
    },
  )

  server.post(
    '/catalog/management/item-diagnoses/v1',
    function (this: MirageServer, schema, request) {
      const body = JSON.parse(request.requestBody)
      const attrs = body.data?.attributes ?? {}

      if (!attrs.item_id) {
        return createErrorResponse({
          status: 422,
          errors: [
            {
              detail: 'Item is required',
              pointer: '/data/attributes/item_id',
            },
          ],
        })
      }

      const itemId = Number(attrs.item_id)
      const departmentId = attrs.department_id
        ? Number(attrs.department_id)
        : null
      const diagnosisIds = attrs.diagnosis_ids || []

      const item = getItemObject(schema, itemId)
      const department = getDepartmentObject(schema, departmentId)
      const diagnoses = getDiagnosesArray(schema, diagnosisIds)

      const group = schema.create('itemDiagnosisGroup', {
        item_id: itemId,
        item: item || { id: itemId, name: `Item ${itemId}`, type: 'PROCEDURE' },
        department_id: departmentId,
        department,
        diagnosis_ids: diagnosisIds,
        diagnoses,
      })

      return { data: serializeGroup(group) }
    },
  )

  server.patch(
    '/catalog/management/item-diagnoses/v1/:id',
    function (this: MirageServer, schema, request) {
      const group = schema.find('itemDiagnosisGroup', request.params.id)

      if (!group) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Item diagnosis group not found' }],
        })
      }

      const body = JSON.parse(request.requestBody)
      const attrs = body.data?.attributes ?? {}

      const updates: Partial<ItemDiagnosisGroupMockData> = {}

      if (attrs.diagnosis_ids !== undefined) {
        updates.diagnosis_ids = attrs.diagnosis_ids
        updates.diagnoses = getDiagnosesArray(schema, attrs.diagnosis_ids)
      }

      if (attrs.item_id !== undefined) {
        const itemId = Number(attrs.item_id)
        updates.item_id = itemId
        updates.item = getItemObject(schema, itemId)
      }

      if (attrs.department_id !== undefined) {
        const departmentId = attrs.department_id
          ? Number(attrs.department_id)
          : null
        updates.department_id = departmentId
        updates.department = getDepartmentObject(schema, departmentId)
      }

      group.update(updates)

      return { data: serializeGroup(group) }
    },
  )

  server.delete(
    '/catalog/management/item-diagnoses/v1/:id',
    function (this: MirageServer, schema, request) {
      const group = schema.find('itemDiagnosisGroup', request.params.id)

      if (!group) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: 'Item diagnosis group not found' }],
        })
      }

      group.destroy()
      return new Response(204)
    },
  )
}

export function seeds(server: MockServer) {
  const departments = server.schema.all('department')
  const firstDept = departments.models[0]
  const secondDept = departments.models[1]

  server.create('itemDiagnosisGroup', {
    id: '1',
    item_id: 101,
    item: { id: 101, name: 'Annual Vaccination', type: 'PROCEDURE' },
    department_id: null,
    department: null,
    diagnosis_ids: ['1', '2', '3'],
    diagnoses: [
      { id: '1', name: 'Canine Distemper' },
      { id: '2', name: 'Rabies' },
      { id: '3', name: 'Parvovirus' },
    ],
  })

  server.create('itemDiagnosisGroup', {
    id: '2',
    item_id: 102,
    item: { id: 102, name: 'Dental Cleaning', type: 'PROCEDURE' },
    department_id: firstDept ? Number(firstDept.id) : 1,
    department: {
      id: firstDept ? Number(firstDept.id) : 1,
      name: firstDept?.attrs.name || 'Main Clinic',
      is_central: firstDept?.attrs.is_central || false,
    },
    diagnosis_ids: ['4', '5'],
    diagnoses: [
      { id: '4', name: 'Periodontal Disease' },
      { id: '5', name: 'Gingivitis' },
    ],
  })

  server.create('itemDiagnosisGroup', {
    id: '3',
    item_id: 103,
    item: { id: 103, name: 'Blood Panel', type: 'LABORATORY' },
    department_id: secondDept ? Number(secondDept.id) : 2,
    department: {
      id: secondDept ? Number(secondDept.id) : 2,
      name: secondDept?.attrs.name || 'Laboratory',
      is_central: secondDept?.attrs.is_central || false,
    },
    diagnosis_ids: ['6'],
    diagnoses: [{ id: '6', name: 'Anaemia' }],
  })

  server.create('itemDiagnosisGroup', {
    id: '4',
    item_id: 104,
    item: { id: 104, name: 'X-Ray Examination', type: 'IMAGING' },
    department_id: null,
    department: null,
    diagnosis_ids: ['7', '8'],
    diagnoses: [
      { id: '7', name: 'Fracture' },
      { id: '8', name: 'Hip Dysplasia' },
    ],
  })

  server.create('itemDiagnosisGroup', {
    id: '5',
    item_id: 105,
    item: { id: 105, name: 'Skin Scraping', type: 'PROCEDURE' },
    department_id: firstDept ? Number(firstDept.id) : 1,
    department: {
      id: firstDept ? Number(firstDept.id) : 1,
      name: firstDept?.attrs.name || 'Main Clinic',
      is_central: true,
    },
    diagnosis_ids: ['9', '10', '11'],
    diagnoses: [
      { id: '9', name: 'Dermatitis' },
      { id: '10', name: 'Mange' },
      { id: '11', name: 'Ringworm' },
    ],
  })
}
